import { z } from 'zod';
import { Privilegio } from '../auth';
import {
  dataOraSchema,
  emailSchema,
  idSchema,
  idSelezioneSchema,
  paginationQuerySchema,
  telefonoSchema,
} from '../common';

/** Utente = membro dello staff Evolfin. Identificato dall'email (univoca). */

export const utenteCreateSchema = z.object({
  nome: z.string().trim().min(1).max(80),
  cognome: z.string().trim().min(1).max(80),
  email: emailSchema,
  telefono: telefonoSchema.optional(),
  /** Il ruolo (dato) di cui l'utente eredita il set di privilegi. */
  ruoloId: idSelezioneSchema('Seleziona un ruolo'),
  /** Privilegi EXTRA rispetto al ruolo: solo additivi (mai sottrattivi). */
  privilegi: z.array(Privilegio).default([]),
});
export type UtenteCreate = z.infer<typeof utenteCreateSchema>;

/**
 * Modifica admin: NON include l'email (identificativo di login univoco,
 * immutabile) né la password (l'utente la imposta da sé via invito/reset).
 * Ruolo e privilegi extra restano gestibili solo da chi ha `can_write_utenti`.
 */
export const utenteUpdateSchema = utenteCreateSchema.omit({ email: true }).partial();
export type UtenteUpdate = z.infer<typeof utenteUpdateSchema>;

/** Filtro sullo stato dell'utente nella lista. */
export const FiltroStatoUtente = z.enum(['attivi', 'disattivati', 'tutti']);
export type FiltroStatoUtente = z.infer<typeof FiltroStatoUtente>;

/** Filtro sulla 2FA nella lista. */
export const FiltroDuefa = z.enum(['tutti', 'attiva', 'disattiva']);
export type FiltroDuefa = z.infer<typeof FiltroDuefa>;

/** Query della lista utenti: paginazione + filtri (stato, 2FA, ruolo). */
export const utentiQuerySchema = paginationQuerySchema.extend({
  stato: FiltroStatoUtente.default('attivi'),
  duefa: FiltroDuefa.default('tutti'),
  /** Filtra per ruolo (id). Assente = tutti i ruoli. */
  ruoloId: idSchema.optional(),
});
export type UtentiQuery = z.infer<typeof utentiQuerySchema>;

/**
 * Tipo di token monouso per impostare la password: `reset` (password dimenticata)
 * o `invito` (onboarding di un nuovo utente, scadenza più lunga). Lista
 * strutturale stabile → `text` + questo enum + `CHECK` a DB (convenzione).
 */
export const TipoTokenUtente = z.enum(['reset', 'invito']);
export type TipoTokenUtente = z.infer<typeof TipoTokenUtente>;

/**
 * Esito dell'ispezione di un token di attivazione/reset (rotta pubblica): dice
 * alla pagina "imposta password" se il link è valido e con che copy renderla
 * (invito = primo accesso, reset = password dimenticata). `nome` per il saluto.
 */
export const tokenInfoSchema = z.object({
  valido: z.boolean(),
  tipo: TipoTokenUtente.nullable(),
  nome: z.string().nullable(),
});
export type TokenInfo = z.infer<typeof tokenInfoSchema>;

/**
 * Immagine profilo (avatar). L'upload è **self-service** e passa dal proxy API
 * (la CSP `*-src 'self'` vieta il traffico diretto verso lo storage): tipi e
 * dimensione massima vivono qui, così API e web validano con gli STESSI limiti.
 */
export const TIPI_IMMAGINE_PROFILO = ['image/png', 'image/jpeg', 'image/webp'] as const;
export type TipoImmagineProfilo = (typeof TIPI_IMMAGINE_PROFILO)[number];
/** Tetto di dimensione dell'avatar: 2 MB (immagini piccole, proxy sull'API). */
export const MAX_BYTES_IMMAGINE_PROFILO = 2 * 1024 * 1024;

/**
 * Profilo self-service: l'utente modifica i PROPRI dati anagrafici. NON include
 * l'email (identificativa, immutabile) né ruolo/privilegi (li gestisce un admin).
 * L'immagine profilo si carica/rimuove con endpoint dedicati (non da qui).
 */
export const profiloUpdateSchema = z.object({
  nome: z.string().trim().min(1).max(80),
  cognome: z.string().trim().min(1).max(80),
  telefono: telefonoSchema.optional(),
});
export type ProfiloUpdate = z.infer<typeof profiloUpdateSchema>;

/** Lettura del profilo self-service (l'email resta nel Principal). */
export const profiloSchema = z.object({
  nome: z.string(),
  cognome: z.string(),
  telefono: z.string().nullable(),
  /** `true` se l'utente ha un'immagine profilo (serve a scegliere avatar vs iniziali). */
  haImmagine: z.boolean(),
});
export type Profilo = z.infer<typeof profiloSchema>;

/** Rappresentazione pubblica dell'utente: mai la password, mai il secret 2FA. */
export const utenteSchema = z.object({
  id: idSchema,
  nome: z.string(),
  cognome: z.string(),
  email: z.string(),
  telefono: z.string().nullable(),
  ruoloId: idSchema,
  /** Nome del ruolo, denormalizzato per la visualizzazione. */
  ruolo: z.string(),
  /** Privilegi extra dell'utente (additivi al ruolo); non gli effettivi. */
  privilegi: z.array(Privilegio),
  duefaAttiva: z.boolean(),
  /** `true` se l'utente ha un'immagine profilo (avatar servito da `/api/utenti/:id/avatar`). */
  haImmagine: z.boolean(),
  /** Ultimo login riuscito; `null` se non ha mai effettuato l'accesso (es. invito in sospeso). */
  ultimoAccessoIl: dataOraSchema.nullable(),
  /** Soft delete: l'utente non viene mai cancellato fisicamente. */
  disattivatoIl: dataOraSchema.nullable(),
  creatoIl: dataOraSchema,
});
export type Utente = z.infer<typeof utenteSchema>;

export const passwordSchema = z
  .string()
  .min(12, 'La password deve avere almeno 12 caratteri')
  .max(128);

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password obbligatoria'),
  /** Secondo fattore TOTP, richiesto solo se l'utente ha la 2FA attiva. */
  codiceTotp: z
    .string()
    .trim()
    .regex(/^\d{6}$/)
    .optional(),
  /** Codice di recupero 2FA, alternativo al TOTP se si perde l'authenticator. */
  codiceRecupero: z.string().trim().optional(),
  /** Sessione lunga su questo dispositivo (cookie a scadenza estesa). */
  ricordami: z.boolean().optional().default(false),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const richiestaResetPasswordSchema = z.object({ email: emailSchema });
export type RichiestaResetPassword = z.infer<typeof richiestaResetPasswordSchema>;

export const resetPasswordSchema = z.object({
  token: z.string().min(20),
  password: passwordSchema,
});
export type ResetPassword = z.infer<typeof resetPasswordSchema>;

export const cambioPasswordSchema = z.object({
  passwordAttuale: z.string().min(1),
  passwordNuova: passwordSchema,
});
export type CambioPassword = z.infer<typeof cambioPasswordSchema>;

/**
 * API key: credenziale alternativa al cookie sulle stesse rotte.
 * È sempre legata a un principal, così guard e audit non cambiano.
 */
export const apiKeyCreateSchema = z.object({
  nome: z.string().trim().min(1).max(120),
  principalId: idSelezioneSchema('Seleziona un utente'),
  scopi: z.array(z.string().trim().min(1)).optional(),
  scadeIl: dataOraSchema.optional(),
});
export type ApiKeyCreate = z.infer<typeof apiKeyCreateSchema>;

/** Restituita UNA SOLA VOLTA alla creazione: a DB restano solo prefisso e hash. */
export const apiKeyCreatedSchema = z.object({
  id: idSchema,
  nome: z.string(),
  prefisso: z.string(),
  chiave: z.string(),
});
export type ApiKeyCreated = z.infer<typeof apiKeyCreatedSchema>;

/** Rappresentazione in lettura: MAI il segreto né l'hash, solo i metadati. */
export const apiKeySchema = z.object({
  id: idSchema,
  nome: z.string(),
  prefisso: z.string(),
  scadeIl: dataOraSchema.nullable(),
  revocatoIl: dataOraSchema.nullable(),
  creatoIl: dataOraSchema,
});
export type ApiKeyVista = z.infer<typeof apiKeySchema>;

/* ── 2FA TOTP (opt-in per utente) ──────────────────────────────────────────── */

/** Codice a 6 cifre generato dall'app di autenticazione. */
export const codiceTotpSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Il codice deve avere 6 cifre');

/**
 * Restituito dall'avvio dell'enrollment: il secret (per l'inserimento manuale),
 * l'URI `otpauth://` e il QR come data-URL da usare come sorgente immagine. Va
 * mostrato una sola volta; l'attivazione poi conferma con un codice.
 */
export const duefaSetupSchema = z.object({
  secret: z.string(),
  otpauthUrl: z.string(),
  qrDataUrl: z.string(),
});
export type DuefaSetup = z.infer<typeof duefaSetupSchema>;

/** Attivazione: conferma il possesso dell'app con un primo codice valido. */
export const duefaAttivazioneSchema = z.object({ codice: codiceTotpSchema });
export type DuefaAttivazione = z.infer<typeof duefaAttivazioneSchema>;

/** Disattivazione: richiede la password (recupero se si perde l'authenticator). */
export const duefaDisattivazioneSchema = z.object({ password: z.string().min(1) });
export type DuefaDisattivazione = z.infer<typeof duefaDisattivazioneSchema>;

/** Stato 2FA dell'utente: letto dalla pagina account (non sta nel Principal). */
export const duefaStatoSchema = z.object({
  attiva: z.boolean(),
  /** Codici di recupero ancora validi (0 se la 2FA non è attiva). */
  codiciRimasti: z.number().int().nonnegative(),
});
export type DuefaStato = z.infer<typeof duefaStatoSchema>;

/**
 * Codici di recupero in chiaro: restituiti UNA SOLA VOLTA all'attivazione della
 * 2FA o alla rigenerazione. A DB restano solo gli hash.
 */
export const duefaCodiciSchema = z.object({ codici: z.array(z.string()) });
export type DuefaCodici = z.infer<typeof duefaCodiciSchema>;
